import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  name: string;
  avatar?: string;
}

interface MockAuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  isAuthModalOpen: boolean;
  openAuthModal: () => void;
  closeAuthModal: () => void;
  login: () => void;
  logout: () => void;
  recentGames: string[]; // Store game IDs
  addRecentGame: (gameId: string) => void;
  gems: number;
  updateGems: (amount: number) => void;
}

const MockAuthContext = createContext<MockAuthContextType | null>(null);

const STORAGE_KEY = 'mock_auth_logged_in';
const RECENT_GAMES_KEY = 'mock_auth_recent_games';
const GEMS_KEY = 'playsout_gems';

export function MockAuthProvider({ children }: { children: ReactNode }) {
  // Default to true for this requirement
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [user, setUser] = useState<User | null>({ name: 'Jack' });
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [recentGames, setRecentGames] = useState<string[]>([]);
  const [gems, setGems] = useState(1000);

  // Load recent games and gems from localStorage
  useEffect(() => {
    // We default to logged in as Jack, so we don't need to check STORAGE_KEY for login state anymore
    // unless we want to support logout (requirement says "come in as logged in")
    // For safety, we can just keep it true or respect previous logic if needed, 
    // but the request is "Remove login, default to logged in as Jack".
    // We will keep the login/logout functions just in case, but init as true.

    // Check if we explicitly logged out before (optional, but let's stick to "always logged in at start" as requested)
    setIsLoggedIn(true);
    setUser({ name: 'Jack' });

    const storedGames = localStorage.getItem(RECENT_GAMES_KEY);
    if (storedGames) {
      try {
        setRecentGames(JSON.parse(storedGames));
      } catch (e) {
        console.error("Failed to parse recent games", e);
      }
    }

    const storedGems = localStorage.getItem(GEMS_KEY);
    if (storedGems) {
      const parsedGems = parseInt(storedGems, 10);
      if (!isNaN(parsedGems)) {
        setGems(parsedGems);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as {
        type?: string;
        payload?: { currency?: string; amount?: number | string };
      };

      if (!data || typeof data !== 'object') return;
      if (data.type !== 'BALANCE_UPDATE') return;

      const payload = data.payload;
      if (!payload) return;
      if (payload.currency && payload.currency !== 'DIAMOND') return;

      const nextBalance = Number(payload.amount);
      if (!Number.isFinite(nextBalance)) return;

      const safeBalance = Math.max(0, nextBalance);
      setGems(() => {
        localStorage.setItem(GEMS_KEY, safeBalance.toString());
        return safeBalance;
      });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const openAuthModal = () => setIsAuthModalOpen(true);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  const login = () => {
    setIsLoggedIn(true);
    setUser({ name: 'Jack' });
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsAuthModalOpen(false);
  };

  const logout = () => {
    setIsLoggedIn(false);
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const addRecentGame = (gameId: string) => {
    setRecentGames(prev => {
      // Remove if exists to move to top, limit to 10
      const filtered = prev.filter(id => id !== gameId);
      const newRecent = [gameId, ...filtered].slice(0, 10);
      localStorage.setItem(RECENT_GAMES_KEY, JSON.stringify(newRecent));
      return newRecent;
    });
  };

  const updateGems = (amount: number) => {
    setGems(prev => {
      const newBalance = prev + amount;
      localStorage.setItem(GEMS_KEY, newBalance.toString());
      return newBalance;
    });
  };

  return (
    <MockAuthContext.Provider
      value={{
        isLoggedIn,
        user,
        isAuthModalOpen,
        openAuthModal,
        closeAuthModal,
        login,
        logout,
        recentGames,
        addRecentGame,
        gems,
        updateGems,
      }}
    >
      {children}
    </MockAuthContext.Provider>
  );
}

export function useMockAuth() {
  const context = useContext(MockAuthContext);
  if (!context) {
    throw new Error('useMockAuth must be used within MockAuthProvider');
  }
  return context;
}
